import * as signalR from "@microsoft/signalr";
import Logger from "../utils/Logger";

class SignalRService {
  connection = null;
  hubUrl =
    process.env.REACT_APP_SIGNALR_URL || "http://localhost:5220/hub/signalr";

  // Adjust if you change Redis presence TTL server-side
  _pingIntervalMs = 30_000;

  constructor(addNotification, getToken, isAuthenticated) {
    this.addNotification = addNotification; // kept for compatibility
    this.getToken = getToken;
    this.isAuthenticated = isAuthenticated;

    Logger.log("SignalRService constructor called");
    Logger.log("SignalR hub URL:", this.hubUrl);

    if (!this.hubUrl) {
      throw new Error(
        "SignalR hub URL not provided (check your env vars or fallback)."
      );
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: async () => {
          try {
            const token = this.getToken ? this.getToken() : undefined;
            Logger.log(
              "SignalR using token:",
              token ? "Token present" : "No token"
            );
            // IMPORTANT: return undefined, not null
            return token ?? undefined;
          } catch (err) {
            Logger.error("Failed to get token for SignalR:", err);
            return undefined;
          }
        },
      })
      // Explicit reconnect delays to avoid stampedes
      .withAutomaticReconnect([0, 2000, 10000, 30000, 30000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Align client keepalive/timeouts with server (KeepAliveInterval=15s)
    this.connection.keepAliveIntervalInMilliseconds = 15_000; // client pings if idle
    this.connection.serverTimeoutInMilliseconds = 60_000; // expect a ping/traffic within 60s

    // Clean up timers when the connection fully closes
    this.connection.onclose(() => {
      Logger.log("SignalR connection closed.");
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    });

    this.connection.onreconnecting((err) => {
      Logger.warn("SignalR reconnecting...", err);
    });
  }

  async start() {
    // Prevent concurrent starts
    if (this._starting) return this._starting;

    const state = this.connection.state;

    if (state === signalR.HubConnectionState.Connected) {
      Logger.log("SignalR already connected.");
      return;
    }

    if (state === signalR.HubConnectionState.Connecting) {
      Logger.log("SignalR is already connecting. Skipping wait.");
      return;
    }

    this._starting = (async () => {
      try {
        if (!this.isAuthenticated || !this.isAuthenticated()) {
          throw new Error(
            "User is not authenticated. Cannot connect to SignalR hub."
          );
        }
        await this.connection.start();
        Logger.log("SignalR connection started successfully.");
      } catch (error) {
        Logger.error("Error starting SignalR connection:", error);
        if (
          typeof error?.message === "string" &&
          (error.message.includes("Unauthorized") ||
            error.message.includes("401"))
        ) {
          Logger.error(
            "SignalR connection failed due to authorization. Check if token is valid."
          );
        }
        throw error;
      } finally {
        this._starting = null;
      }
    })();

    return this._starting;
  }

  async waitForDisconnected(timeoutMs = 5000) {
    const start = Date.now();
    while (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      if (Date.now() - start > timeoutMs) {
        throw new Error("Timed out waiting for SignalR to disconnect.");
      }
      await new Promise((res) => setTimeout(res, 100));
    }
  }

  async stop() {
    try {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
      await this.connection.stop();
      Logger.log("SignalR connection stopped.");
    } catch (error) {
      Logger.error("Error stopping SignalR connection:", error);
    }
  }

  onMessage(methodName, callback) {
    try {
      // Remove any existing handlers for this method name
      this.connection.off(methodName);
      // Add the new callback
      this.connection.on(methodName, callback);
    } catch (error) {
      Logger.error(`Error setting up SignalR message handler: ${error}`);
    }
  }
}

export default SignalRService;
